import { useDivision } from '@ab-core-functions/get-divisions-by-business-unit';
import { useIsFeatureVisible } from '@ab-core/functions';
import type { MappedProduct } from '@ab-core/graphql/dist';
import { useAuth } from '@ab-core/hooks';
import { useGetProductBySkuV2 } from '@ab-core/hooks-v2';
import type { ProductTagsProps } from './index';

export type GetProductTagsPropsProps = {
    product: {
        isPromotion?: boolean;
        topProduct?: boolean;
        isTecselect?: boolean;
        isAbakus?: boolean;
        isAbakusPlus?: boolean;
        isFiveYearGuarantee?: boolean;
        is125Vip?: boolean;
        is125Premium?: boolean;
        isAdditionalSalesPromotion?: boolean;
        scoreCardIndex?: number;
        purchase?: boolean;
        isItShop?: boolean;
        successorProduct?: MappedProduct['successorProduct'];
        ean?: MappedProduct['ean'];
    };
};

const BUSINESS_UNIT_SMART_BUILDINGS = 'SB';

const useSuccessorProduct = (successorSku?: string): string | undefined => {
    const { product } = useGetProductBySkuV2({ sku: successorSku, skip: !successorSku });

    if (!successorSku || !product?.isOnlineAvailable) {
        return undefined;
    }

    return successorSku;
};

export const getProductTagsProps = (props: GetProductTagsPropsProps): ProductTagsProps => {
    const { product } = props;
    const { user } = useAuth();
    const { isFeatureVisible } = useIsFeatureVisible();
    const isPremiumShop = isFeatureVisible('praemienShop');
    const isAbakus = Boolean(user?.hasAbakus && product?.isAbakus);
    const isAbakusPlus = Boolean(user?.hasAbakus && product?.isAbakusPlus);
    const isAktion = Boolean(user && product?.isPromotion);
    const isPremium = Boolean(user && product?.topProduct);
    const isTecselect = Boolean(product?.isTecselect);
    const scoreCardIndex = product?.scoreCardIndex || 0;
    const isFiveYearGuarantee = Boolean(user && product?.isFiveYearGuarantee);
    const is125Vip = Boolean(isPremiumShop && user && product.is125Vip);
    const is125Premium = Boolean(isPremiumShop && user && product.is125Premium);
    const isAdditionalSalesPromotion = Boolean(
        useDivision() === BUSINESS_UNIT_SMART_BUILDINGS && product.isAdditionalSalesPromotion
    );
    const purchase = Boolean(user && product?.purchase);
    const isItShop = Boolean(product?.isItShop);
    const successorProduct = useSuccessorProduct(product?.successorProduct || undefined);
    const ean = product?.ean || undefined;

    return {
        isAbakus,
        isAbakusPlus,
        isAktion,
        isPremium,
        isTecselect,
        scoreCardIndex,
        isFiveYearGuarantee,
        is125Vip,
        is125Premium,
        isAdditionalSalesPromotion,
        purchase,
        isItShop,
        successorProduct,
        ean
    };
};

export const getFallbackState = (tags?: ProductTagsProps): boolean => {
    if (!tags) {
        return false;
    }

    const values = Object.values(tags);
    const areAllFalse = values.every((value) => !value);

    return areAllFalse;
};
